import {
  Stack,
  Flex,
  Button,
  Text,
  VStack,
  useBreakpointValue,
  Heading,
  Box,
  Icon,
  HStack,
  keyframes,
} from "@chakra-ui/react";
import ChakraUIRenderer from "chakra-ui-markdown-renderer";
import { useReducedMotion } from "framer-motion";
import Image from "next/image";
import Router from "next/router";
import { NextSeo } from "next-seo";
import { FiArrowRight, FiShield, FiZap } from "react-icons/fi";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import style from "../../styles/markdown.module.css";
import BCAdventureSmartOffer from "../buttons/Klaviyo/bc-adventure-smart";
import KlaviyoJoinedForcesOffer from "../buttons/Klaviyo/joined-forces";
import KlaviyoOutdoorCouncilOffer from "../buttons/Klaviyo/outdoor-council";
import KlaviyoZoleoOffer from "../buttons/Klaviyo/zoleo-offer";
import IntercomHelp from "../IntercomHelp";
import Banner from "lib/components/home/Banner";
import CTASection from "lib/components/home/CTASection";
import Feature1 from "lib/components/home/Feature1";
import Feature2 from "lib/components/home/Feature2";
import Feature3 from "lib/components/home/Feature3";

// Hoisted to module scope — ChakraUIRenderer() is expensive to call on every render
const chakraMarkdownComponents = ChakraUIRenderer();

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

// GPU-compositable pulse: animates opacity on a static gradient overlay
// instead of background-position (which forces layout recalculation)
const gradientPulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

export type LandingProps = {
  page_title: string;
  headline: string;
  subheading: string;
  header_background: string;
  form: number;
};

interface LandingHeroProps {
  heading: string;
  subheading: string;
  form: number;
  headerBackground?: string;
}

const LandingPageHero = ({
  heading,
  subheading,
  form,
  headerBackground,
}: LandingHeroProps) => {
  const shouldReduceMotion = useReducedMotion();
  const floatAnimation = shouldReduceMotion
    ? undefined
    : `${float} 6s ease-in-out infinite`;

  const imageUrl = headerBackground
    ? `${process.env.NEXT_PUBLIC_DIRECTUS_URL}/assets/${headerBackground}`
    : null;

  return (
    <Flex
      w="full"
      minH="100vh"
      position="relative"
      overflow="hidden"
      bg="#0a0f1a"
    >
      {imageUrl ? (
        <>
          {/* CMS-provided background image */}
          <Image
            src={imageUrl}
            alt=""
            fill
            priority
            quality={60}
            sizes="100vw"
            style={{ objectFit: "cover", objectPosition: "center" }}
          />
          {/* Dark overlay for text readability */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="rgba(0, 0, 0, 0.6)"
          />
        </>
      ) : (
        <>
          {/* Fallback: gradient background + animated orbs */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bgGradient="linear(135deg, #0a0f1a 0%, #1a2a4a 50%, #0a0f1a 100%)"
          />
          {!shouldReduceMotion && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bgGradient="linear(135deg, #1a1f3a 0%, #0f1829 50%, #1a2a4a 100%)"
              animation={`${gradientPulse} 15s ease infinite`}
            />
          )}
          <Box
            position="absolute"
            top="10%"
            left="10%"
            w={{ base: "200px", md: "400px" }}
            h={{ base: "200px", md: "400px" }}
            borderRadius="full"
            bg="linear-gradient(135deg, rgba(255, 79, 0, 0.3), rgba(255, 120, 50, 0.1))"
            filter="blur(80px)"
            animation={floatAnimation}
          />
          <Box
            position="absolute"
            bottom="20%"
            right="5%"
            w={{ base: "150px", md: "300px" }}
            h={{ base: "150px", md: "300px" }}
            borderRadius="full"
            bg="linear-gradient(135deg, rgba(0, 150, 255, 0.2), rgba(100, 200, 255, 0.1))"
            filter="blur(60px)"
            animation={
              shouldReduceMotion
                ? undefined
                : `${float} 8s ease-in-out infinite`
            }
          />
          <Box
            position="absolute"
            top="60%"
            left="60%"
            w={{ base: "100px", md: "200px" }}
            h={{ base: "100px", md: "200px" }}
            borderRadius="full"
            bg="linear-gradient(135deg, rgba(255, 79, 0, 0.2), rgba(255, 150, 100, 0.1))"
            filter="blur(40px)"
            animation={
              shouldReduceMotion
                ? undefined
                : `${float} 10s ease-in-out infinite`
            }
          />
        </>
      )}

      {/* Grid pattern overlay */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        opacity={0.03}
        backgroundImage="linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)"
        backgroundSize="50px 50px"
      />

      <VStack
        w="full"
        justify="center"
        px={useBreakpointValue({ base: 6, md: 8 })}
        minH="100vh"
        position="relative"
        zIndex={1}
      >
        <Stack maxW="4xl" align="center" spacing={{ base: 8, md: 12 }}>
          <Heading
            color="white"
            fontWeight={700}
            fontSize={{ base: "4xl", sm: "5xl", md: "7xl" }}
            lineHeight="1.1"
            textAlign="center"
            letterSpacing="-0.02em"
          >
            {heading}
          </Heading>

          <Box
            color="text.on-black-secondary"
            maxW="2xl"
            textAlign="center"
            fontSize={{ base: "lg", md: "xl" }}
            lineHeight="1.8"
          >
            <ReactMarkdown
              components={chakraMarkdownComponents}
              className={style.reactMarkDown}
              remarkPlugins={[remarkGfm]}
              skipHtml
            >
              {subheading}
            </ReactMarkdown>
          </Box>

          {/* CTA Buttons */}
          <Flex
            direction={{ base: "column", sm: "row" }}
            gap={4}
            wrap="wrap"
            justify="center"
            pt={2}
          >
            <Button
              size="lg"
              bg="brand.500"
              color="white"
              px={8}
              py={7}
              fontSize="md"
              fontWeight={600}
              borderRadius="xl"
              _hover={{
                bg: "brand.600",
                transform: "translateY(-2px)",
                boxShadow: "0 20px 40px rgba(255, 79, 0, 0.3)",
              }}
              transition="all 0.3s ease"
              onClick={() => Router.push("/how-it-works")}
              rightIcon={<FiArrowRight />}
            >
              How It Works
            </Button>
            {form === 0 ? <KlaviyoZoleoOffer /> : null}
            {form === 1 ? <KlaviyoJoinedForcesOffer /> : null}
            {form === 2 ? <BCAdventureSmartOffer /> : null}
            {form === 3 ? <KlaviyoOutdoorCouncilOffer /> : null}
          </Flex>

          {/* Trust indicators */}
          <HStack
            spacing={{ base: 6, md: 12 }}
            pt={4}
            flexWrap="wrap"
            justify="center"
          >
            <HStack color="text.on-black-muted" spacing={2}>
              <Icon as={FiShield} />
              <Text fontSize="sm">24/7 Monitoring</Text>
            </HStack>
            <HStack color="text.on-black-muted" spacing={2}>
              <Icon as={FiShield} />
              <Text fontSize="sm">Trusted Alerts</Text>
            </HStack>
            <HStack color="text.on-black-muted" spacing={2}>
              <Icon as={FiZap} />
              <Text fontSize="sm">Instant Delivery</Text>
            </HStack>
          </HStack>
        </Stack>
      </VStack>
    </Flex>
  );
};

const Landing = ({
  page_title,
  headline,
  subheading,
  header_background,
  form,
}: LandingProps) => {
  return (
    <>
      <NextSeo title={page_title} description="Adiona Alert" />
      <Banner />
      <LandingPageHero
        heading={headline}
        subheading={subheading}
        form={form}
        headerBackground={header_background}
      />
      <Feature1 />
      <Feature2 />
      <Feature3 />
      <CTASection />
      <IntercomHelp />
    </>
  );
};

export default Landing;
